import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaUrl, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Seo } from "../components/Seo";
import { InlinePlayer } from "../components/InlinePlayer";

export default function Profile() {
  const { user, setUser, refresh } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [handicap, setHandicap] = useState("");
  const [homeCourse, setHomeCourse] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [myVideos, setMyVideos] = useState([]);

  const loadVideos = useCallback(async () => {
    try {
      const { data } = await api.get("/videos/mine");
      setMyVideos(data);
    } catch (e) {
      console.error("Failed to load my videos:", e);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setHandicap(user.handicap || "");
      setHomeCourse(user.home_course || "");
      setBio(user.bio || "");
      loadVideos();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user === null) nav("/login");
  }, [user, nav]);

  if (!user) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(""); setMsg("");
    try {
      const { data } = await api.put("/profile", { handicap, home_course: homeCourse, bio });
      setUser(data);
      setMsg("Profile saved.");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const onAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true); setErr(""); setMsg("");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data);
      await refresh();
      setMsg("Profile photo updated.");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setUploading(false);
    }
  };

  const avatar = mediaUrl(user.avatar_url);

  return (
    <div data-testid="profile-page" className="min-h-screen pt-32 pb-24 px-6">
      <Seo title="My Profile" description="Manage your Dr. Cuts profile." path="/profile" noindex />
      <div className="max-w-xl mx-auto">
        <div className="overline">Your locker</div>
        <h1 className="font-display text-5xl mt-3">My Profile</h1>

        <div className="flex items-center gap-5 mt-10">
          <div className="w-24 h-24 rounded-full overflow-hidden border border-[#C9A227]/40 flex items-center justify-center bg-white/[0.04]">
            {avatar ? (
              <img src={avatar} alt="avatar" data-testid="profile-avatar-img" className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-3xl text-[#C9A227]" style={{ color: user.avatar_color }}>
                {(user.name || user.email).slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onAvatar} className="hidden" data-testid="profile-avatar-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-ghost !py-2 !px-4 text-xs" data-testid="profile-avatar-btn">
              {uploading ? "Uploading..." : "Change photo"}
            </button>
            <div className="text-white/40 text-xs mt-2">JPG/PNG, up to 5MB</div>
          </div>
        </div>

        <form onSubmit={save} className="mt-10 space-y-6">
          <div>
            <label className="overline block mb-2" style={{ color: "#A0B0A8" }}>Name</label>
            <input value={user.name} disabled className="input-dark opacity-60" />
          </div>
          <div>
            <label className="overline block mb-2" style={{ color: "#A0B0A8" }}>Handicap</label>
            <input data-testid="profile-handicap" value={handicap} onChange={(e) => setHandicap(e.target.value)} maxLength={20} className="input-dark" placeholder="e.g. 8.4 or +1" />
          </div>
          <div>
            <label className="overline block mb-2" style={{ color: "#A0B0A8" }}>Home course</label>
            <input data-testid="profile-home-course" value={homeCourse} onChange={(e) => setHomeCourse(e.target.value)} maxLength={120} className="input-dark" placeholder="Waynesville Inn Golf Club" />
          </div>
          <div>
            <label className="overline block mb-2" style={{ color: "#A0B0A8" }}>Short bio</label>
            <textarea data-testid="profile-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={4} maxLength={600} className="input-dark" placeholder="A little about your game and what you're working on." />
          </div>

          {msg && <div className="text-[#C9A227] text-sm" data-testid="profile-msg">{msg}</div>}
          {err && <div className="text-red-400 text-sm" data-testid="profile-error">{err}</div>}

          <button data-testid="profile-save" disabled={saving} className="btn-primary w-full">
            {saving ? "Saving..." : "Save Profile"}
          </button>
        </form>
      </div>

      <div className="max-w-[1100px] mx-auto mt-20" data-testid="profile-videos-section">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="overline">Your uploads</div>
            <h2 className="font-display text-4xl mt-2">My Swing Videos</h2>
            <p className="text-white/60 mt-2 text-sm">Every swing you've uploaded. Tap any to play right here.</p>
          </div>
          <span className="text-white/40 text-sm" data-testid="profile-videos-count">{myVideos.length} video{myVideos.length === 1 ? "" : "s"}</span>
        </div>

        {myVideos.length === 0 ? (
          <div className="mt-8 p-12 border border-white/10 bg-white/[0.02] text-center" data-testid="profile-videos-empty">
            <p className="text-white/60">You haven't uploaded any swings yet.</p>
            <button onClick={() => nav("/upload")} className="btn-primary mt-6 inline-block">Upload Swing</button>
          </div>
        ) : (
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {myVideos.map((v) => (
              <div key={v.video_id} data-testid={`profile-video-${v.video_id}`} className="border border-white/10 bg-white/[0.02] overflow-hidden">
                <div className="relative aspect-[4/5] bg-black overflow-hidden group">
                  <InlinePlayer videoId={v.video_id} testid={`profile-player-${v.video_id}`} />
                  <span data-testid={`profile-video-status-${v.video_id}`}
                        className={`absolute top-2 left-2 text-[10px] uppercase tracking-widest px-2 py-1 pointer-events-none ${v.has_response ? "bg-[#C9A227] text-[#050A08]" : "bg-white/10 text-white/70"}`}>
                    {v.has_response ? "Responded" : "Pending"}
                  </span>
                </div>
                <div className="p-4 flex items-center justify-between gap-3">
                  <h3 className="font-display text-lg leading-tight truncate">{v.title}</h3>
                  <span className={`text-[10px] uppercase tracking-widest whitespace-nowrap ${v.is_published ? "text-[#C9A227]" : "text-white/40"}`}>
                    {v.is_published ? "● Public" : "● Private"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
